import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const MediaGalleryModal = ({ mediaUrls, onClose, initialIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [mediaArray, setMediaArray] = useState([]);

    useEffect(() => {
        // Handle both string and array formats
        if (Array.isArray(mediaUrls)) {
            setMediaArray(mediaUrls.filter(url => url)); // Filter out empty/null values
        } else if (mediaUrls) {
            setMediaArray([mediaUrls]);
        } else {
            setMediaArray([]);
        }
    }, [mediaUrls]);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handlePrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaArray.length - 1));
    }, [mediaArray.length]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => (prev < mediaArray.length - 1 ? prev + 1 : 0));
    }, [mediaArray.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") handlePrevious();
            if (e.key === "ArrowRight") handleNext();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handlePrevious, handleNext, onClose]);

    if (mediaArray.length === 0) {
        return null;
    }

    const currentMedia = mediaArray[currentIndex];
    const isVideo = currentMedia?.includes("mp4") ||
        currentMedia?.includes("webm") ||
        currentMedia?.includes("video");

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50"
            onClick={onClose}
        >
            <div
                className="relative max-w-7xl max-h-[95vh] w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 text-white transition-colors"
                    aria-label="Close"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Navigation Arrows */}
                {mediaArray.length > 1 && (
                    <>
                        <button
                            onClick={handlePrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 text-white transition-colors"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-8 h-8" />
                        </button>
                        <button
                            onClick={handleNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 text-white transition-colors"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-8 h-8" />
                        </button>
                    </>
                )}

                {/* Media Display */}
                <div className="flex justify-center items-center h-full">
                    {isVideo ? (
                        <video
                            src={currentMedia}
                            className="max-w-full max-h-[95vh] object-contain rounded-lg"
                            controls
                            autoPlay
                        />
                    ) : (
                        <img
                            src={currentMedia}
                            alt={`Media ${currentIndex + 1}`}
                            className="max-w-full max-h-[95vh] object-contain rounded-lg"
                        />
                    )}
                </div>

                {/* Thumbnail Navigation */}
                {mediaArray.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                        <div className="flex gap-2 bg-black bg-opacity-50 rounded-lg p-2 overflow-x-auto max-w-[90vw]">
                            {mediaArray.map((url, index) => {
                                const isThumbnailVideo = url?.includes("mp4") ||
                                    url?.includes("webm") ||
                                    url?.includes("video");
                                return (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentIndex(index)}
                                        className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${index === currentIndex
                                                ? "border-white scale-110"
                                                : "border-transparent opacity-60 hover:opacity-100"
                                            }`}
                                    >
                                        {isThumbnailVideo ? (
                                            <video
                                                src={url}
                                                className="w-full h-full object-cover"
                                                muted
                                            />
                                        ) : (
                                            <img
                                                src={url}
                                                alt={`Thumbnail ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Counter */}
                {mediaArray.length > 1 && (
                    <div className="absolute top-4 left-4 z-10 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
                        {currentIndex + 1} / {mediaArray.length}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MediaGalleryModal;

